import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Stack,
  IconButton,
  Divider,
  alpha,
  useTheme,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Assessment as RiskIcon,
  ArrowForwardIos as ArrowIcon,
  Circle as StatusDot,
  CalendarToday as CalendarIcon,
  Assignment as CarePlanIcon,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { QK } from "../../../base/qk";
import { getRisks } from "../../../api/services/riskProfile";
import { formatDate } from "../../../utils/time";

const getRiskStatus = (impact, likelihood) => {
  const score = (Number(impact) || 0) * (Number(likelihood) || 0);
  if (score >= 15) return {
    label: "EXTREME",
    color: "#DC2626",
    bg: "#FEF2F2",
    dotColor: "#DC2626",
    borderColor: "#FCA5A5"
  };
  if (score >= 8) return {
    label: "HIGH",
    color: "#F59E0B",
    bg: "#FFFBEB",
    dotColor: "#F59E0B",
    borderColor: "#FCD34D"
  };
  if (score >= 4) return {
    label: "MEDIUM",
    color: "#3B82F6",
    bg: "#EFF6FF",
    dotColor: "#3B82F6",
    borderColor: "#93C5FD"
  };
  if (score >= 1) return {
    label: "LOW",
    color: "#10B981",
    bg: "#ECFDF5",
    dotColor: "#10B981",
    borderColor: "#6EE7B7"
  };
  return {
    label: "N/A",
    color: "#94A3B8",
    bg: "#F8FAFC",
    dotColor: "#94A3B8",
    borderColor: "#E2E8F0"
  };
};

const RiskAssessment = () => {
  const theme = useTheme();
  const { id: clientID } = useParams();

  const { data: risks, isLoading } = useQuery(
    [QK.risks, clientID],
    () => getRisks(clientID),
    {
      enabled: !!clientID,
    }
  );

  if (isLoading) {
    return (
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </CardContent>
      </Card>
    );
  }

  const riskList = risks?.results || [];

  return (
    <Card
      sx={{
        borderRadius: "12px",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
        overflow: "hidden",
      }}
    >
      <CardHeader
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: "#FAFBFC",
          borderBottom: "1px solid #F2F4F7",
        }}
        title={
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "6px",
                  bgcolor: alpha(theme.palette.warning.main, 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <RiskIcon sx={{ color: theme.palette.warning.main, fontSize: 16 }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#1A1A2E", fontSize: "0.85rem" }}>
                Risk Assessment
              </Typography>
              <Chip
                label={riskList.length}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.55rem",
                  fontWeight: 500,
                  bgcolor: "#F2F4F7",
                  color: "#667085",
                }}
              />
            </Box>
            <IconButton
              component={Link}
              to={`/${clientID}/assessments`}
              size="small"
              sx={{
                width: 28,
                height: 28,
                bgcolor: "#fff",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                "&:hover": {
                  bgcolor: theme.palette.primary.main,
                  color: "#fff",
                },
                transition: "all 0.2s",
              }}
            >
              <ArrowIcon sx={{ fontSize: 9 }} />
            </IconButton>
          </Box>
        }
      />

      <Divider sx={{ opacity: 0.4 }} />

      <CardContent sx={{ p: 1.5 }}>
        {riskList.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 1.5 }}>
            <Typography variant="body2" sx={{ color: "#667085", fontSize: "0.8rem" }}>
              No risk assessments found
            </Typography>
          </Box>
        ) : (
          <Stack spacing={0.75}>
            {riskList.slice(0, 5).map((risk) => {
              const status = getRiskStatus(risk.latest_impact, risk.latest_likelihood);
              return (
                <Box
                  key={risk.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 0.5,
                    px: 0.5,
                    borderRadius: "4px",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                    },
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#1A1A2E",
                        fontWeight: 500,
                        fontSize: "0.8rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        flex: 1,
                      }}
                    >
                      {risk.risk_name || "Risk"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
                    <Chip
                      label={status.label}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        bgcolor: status.bg,
                        color: status.color,
                        border: `1px solid ${status.borderColor}`,
                        minWidth: 60,
                        justifyContent: "center",
                        "& .MuiChip-label": {
                          px: 0.75,
                        },
                      }}
                      icon={
                        <StatusDot
                          sx={{
                            fontSize: 6,
                            color: status.dotColor,
                            ml: 0.5
                          }}
                        />
                      }
                    />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CarePlanIcon sx={{ fontSize: 11, color: "#98A2B3" }} />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#667085",
                          fontSize: "0.65rem",
                          fontWeight: 400,
                          maxWidth: 80,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {risk.care_plan_name || "N/A"}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CalendarIcon sx={{ fontSize: 11, color: "#98A2B3" }} />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#667085",
                          fontSize: "0.65rem",
                          fontWeight: 400,
                        }}
                      >
                        {formatDate(risk.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
            {riskList.length > 5 && (
              <Typography
                variant="caption"
                sx={{
                  color: "#667085",
                  fontSize: "0.7rem",
                  textAlign: "center",
                  pt: 0.5,
                  fontStyle: "italic",
                }}
              >
                +{riskList.length - 5} more
              </Typography>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default RiskAssessment;